import styled from "styled-components";
import dayjs from "dayjs";
import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import { i18nFilter } from "app/filters/i18nFilter";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";
import type { RawDate } from "app/UtilitiesAndConstants/types/dates";
import { isPartialGraphDataAvailable } from "app/UtilitiesAndConstants/UtilityFunctions/partialData";
import { Marker, StyledAlertIcon } from "pro-features/components/Chart/src/configs/plotLines/ChangeBellWithoutTooltip";
import { LeftFlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { SWReactIcons } from "@similarweb/icons";
import {
    shortMonthAndFullDay,
    shortMonthAndYear,
    shortMonthDayAndYear,
    shortMonthDayAndYearWithDayOfWeek,
} from "@similarweb/pro-logic-components";

export const TooltipPartialText = styled.div`
    font-size: 12px;
    line-height: 16px;
    padding: 4px 8px;
    font-weight: 400;
    background: ${colorsPalettes.carbon["50"]};
    border-radius: 4px;
    margin-top: 8px;
`;
const BellText = styled.span`
    ${setFontVariant("listItem3", colorsPalettes.carbon[300])}
    width: calc(100% - 28px);
    margin-left: 8px;
    white-space: break-spaces;
    padding-right: 16px;
`;
const BellContentWrapper = styled(LeftFlexRow)`
    background-color: ${colorsPalettes.orange[100]};
    height: 48px;
    padding-left: 16px;
    border-radius: 6px;
    align-items: center;
    width: calc(100% - 16px);
    margin-top: 16px;
    margin-bottom: 8px;
    ${Marker} {
        width: 20px;
    }
    ${SWReactIcons} {
        display: flex;
    }
`;
export const getTooltipHeaderElement = (
    timeGranularity: GranularityNames,
    date: RawDate,
    lastSupportedDate: RawDate | undefined,
    asText?: boolean,
    alertMessage?: string,
    hideDayInMonthlyLabel = false,
) => {
    const isPartial = lastSupportedDate ? isPartialGraphDataAvailable(date, timeGranularity, lastSupportedDate) : false;
    const dateMoment = dayjs.utc(date);

    const getBellContent = (text: string) => (
        <BellContentWrapper>
            <Marker>
                <StyledAlertIcon iconName="alerts" />
            </Marker>
            <BellText>{i18nFilter()(text)}</BellText>
        </BellContentWrapper>
    );
    const tooltipGenerators = {
        [GranularityNames.MONTHLY]: () => {
            const untilDateText = dayjs(lastSupportedDate).format(shortMonthAndFullDay);
            const fromDateText = dateMoment.format(shortMonthDayAndYear);
            const UntilText = i18nFilter()("graph.tooltip.until", {
                fromDate: fromDateText,
                untilDate: untilDateText,
            });

            // A whole-month point reads better as "Jun 2026"; the partial "until" range
            // keeps day precision because it points at a specific cut-off date.
            const monthLabel = hideDayInMonthlyLabel ? dateMoment.format(shortMonthAndYear) : fromDateText;
            const text = isPartial ? UntilText : monthLabel;
            const partialText = isPartial ? i18nFilter()("common.tooltip.show.partial.month") : "";
            return { text, partialText };
        },
        [GranularityNames.WEEKLY]: () => {
            const toWeek = dayjs(dateMoment).add(6, "days");
            const toDate = toWeek.isAfter(lastSupportedDate) ? dayjs(lastSupportedDate) : toWeek;
            const fromDateText = dateMoment.format(shortMonthDayAndYear);
            const toDateText = toDate.format(shortMonthDayAndYear);

            const text = i18nFilter()("graph.tooltip.from.date.to.date", {
                fromDate: fromDateText,
                toDate: toDateText,
            });

            const partialText = isPartial ? i18nFilter()("common.tooltip.show.partial.week") : "";
            return { text, partialText };
        },
        default: () => {
            const text = dateMoment.format(shortMonthDayAndYearWithDayOfWeek[0]);
            return { text, partialText: "" };
        },
    };

    const { text, partialText } = (
        (tooltipGenerators as Record<string, () => { text: string; partialText: string }>)[timeGranularity] ||
        tooltipGenerators.default
    )();

    return asText ? (
        text
    ) : (
        <>
            <span>{text}</span>
            {partialText && <TooltipPartialText>{partialText}</TooltipPartialText>}
            {alertMessage && getBellContent(alertMessage)}
        </>
    );
};

export const getTooltipHeader = (
    timeGranularity: GranularityNames,
    points: readonly any[],
    lastSupportedDate?: string,
    asText?: boolean,
    alertMessage?: string,
) =>
    // @ts-ignore FIXME: strict migration - optional lastSupportedDate forwarded to RawDate-required param — widening cascades through isPartialGraphDataAvailable into dateTimeService, out of scope for this wave
    getTooltipHeaderElement(timeGranularity, points[0].x, lastSupportedDate, asText, alertMessage);
